import React, { useState } from 'react';
import { Alert, Button, Box, CircularProgress } from '@mui/material';
import { useAppStore } from '../state/store';
import { meetingDataService } from '../services/meetingDataService';
import { logDebug } from '../utils/errorLogger';

const SaveBar: React.FC = () => {
  const { 
    hasUnsavedChanges, 
    meetingId, 
    tasks, 
    takeaways, 
    originalTasks, 
    originalTakeaways,
    tldr,
    executiveSummary,
    detailedSummary,
    originalTldr,
    originalExecutiveSummary,
    originalDetailedSummary,
    loadFromJson
  } = useAppStore();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!meetingId) {
      setError('No meeting ID available');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      logDebug('Saving changes...', {
        meetingId,
        tasksCount: tasks.length,
        takeawaysCount: takeaways.length
      });

      // Prepare updates payload
      const updates: any = {
        updatedBy: 'user' // This will be replaced by backend with actual user email
      };

      // Collect changed/new tasks
      const changedTasks = tasks.filter(task => {
        const original = originalTasks.find(t => t.id === task.id);
        return !original || JSON.stringify(task) !== JSON.stringify(original);
      });

      // Separate new tasks (for duplication/conversion) from existing tasks
      const newTasks = changedTasks.filter(task => task.isNew);
      const existingTasks = changedTasks.filter(task => !task.isNew);

      if (newTasks.length > 0) {
        updates.newTasks = newTasks.map(task => ({
          id: task.id,
          meetingId: task.meetingId || meetingId,
          title: task.title,
          description: task.description,
          owner: task.owner,
          requester: task.requester,
          due: task.due,
          itemType: task.type, // Backend expects 'itemType', not 'type'
          priority: task.priority,
          tags: task.tags,
          confidence: task.confidence,
          evidence: task.evidence,
          ownerCandidates: task.ownerCandidatesRaw,
          origin: task.origin,
          originalId: task.originalId
        }));
      }

      if (existingTasks.length > 0) {
        updates.tasks = existingTasks.map(task => ({
          id: task.id,
          meetingId: task.meetingId || meetingId,
          title: task.title,
          description: task.description,
          owner: task.owner,
          requester: task.requester,
          due: task.due,
          itemType: task.type, // Backend expects 'itemType', not 'type'
          priority: task.priority,
          tags: task.tags,
          confidence: task.confidence,
          evidence: task.evidence,
          ownerCandidates: task.ownerCandidatesRaw,
          isDeleted: task.isDeleted
        }));
      }

      // Collect changed takeaways
      const changedTakeaways = takeaways.filter(takeaway => {
        const original = originalTakeaways.find(t => t.id === takeaway.id);
        return !original || JSON.stringify(takeaway) !== JSON.stringify(original);
      });

      if (changedTakeaways.length > 0) {
        updates.takeaways = changedTakeaways.map(takeaway => ({
          id: takeaway.id,
          meetingId: meetingId, // Required by backend TakeawayRequest
          title: takeaway.name,
          description: takeaway.description,
          itemType: takeaway.type, // Backend expects 'itemType', not 'type'
          tags: takeaway.tags,
          confidence: takeaway.confidence,
          evidence: takeaway.evidence,
          isDeleted: takeaway.isConverted || takeaway.isDeleted // Soft delete if converted to task or explicitly deleted
        }));
      }

      // Add summary changes if any
      if (tldr !== originalTldr) {
        updates.tldr = tldr;
      }
      if (executiveSummary !== originalExecutiveSummary) {
        updates.executiveSummary = executiveSummary;
      }
      if (detailedSummary !== originalDetailedSummary) {
        updates.detailedSummary = detailedSummary;
      }

      logDebug('Sending updates:', updates);

      // Call API
      await meetingDataService.updateMeetingItems(meetingId, updates);

      // Reload data from backend
      logDebug('Reloading meeting data after save...');
      const freshData = await meetingDataService.loadMeeting(meetingId);

      // Update store with fresh data
      const distillOutput = {
        meeting_brief: freshData.meeting_brief,
        key_points: freshData.key_points,
        actions: freshData.actions,
        themes: freshData.themes || [],
        clarifications: [],
        metrics: {
          total_key_points: freshData.key_points?.length || 0,
          total_actions: freshData.actions?.length || 0,
          total_themes: freshData.themes?.length || 0,
          confidence_score_avg: 0.8
        }
      };
      loadFromJson(distillOutput, meetingId);

      logDebug('Changes saved successfully');
    } catch (err) {
      console.error('Error saving changes:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to save changes';
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    // Reload from original data
    if (!meetingId) return;

    // Transform takeaways to KeyPointJson format
    const keyPoints = originalTakeaways.map(t => ({
      type: t.type,
      title: t.name,
      description: t.description,
      evidence: t.evidence,
      confidence: t.confidence
    }));

    // Pass full task objects with ALL fields preserved (id, isProcessed, etc.)
    const actions = originalTasks.map(t => ({
      id: t.id,
      meetingId: t.meetingId,
      title: t.title,
      description: t.description,
      owner: t.owner, // Full UserField object
      requester: t.requester, // Full UserField object
      ownerCandidates: t.ownerCandidates,
      owner_candidates: t.ownerCandidatesRaw || [],
      due: t.due,
      itemType: t.type, // Use itemType (not type) for the mapper
      priority: t.priority,
      labels: t.tags,
      evidence: t.evidence,
      confidence: t.confidence,
      // Critical fields for task state
      isProcessed: t.isProcessed,
      sentToSalesforceAt: t.sentToSalesforceAt,
      sentToSalesforceBy: t.sentToSalesforceBy,
      origin: t.origin,
      originalId: t.originalId,
      hiddenType: t.hiddenType,
      isNew: t.isNew
    }));

    const distillOutput = {
      meeting_brief: {
        title: '',
        tldr: originalTldr,
        executive_summary: originalExecutiveSummary,
        detailed_summary: originalDetailedSummary
      },
      key_points: keyPoints,
      actions: actions as any, // Cast to any - mapper handles full Task objects
      themes: [],
      clarifications: [],
      metrics: {
        total_key_points: keyPoints.length,
        total_actions: actions.length,
        total_themes: 0,
        confidence_score_avg: 0.8
      }
    };
    loadFromJson(distillOutput as any, meetingId);
    setError(null);
  };

  // Don't show if no unsaved changes
  if (!hasUnsavedChanges()) return null;

  return (
    <Alert 
      severity="warning" 
      sx={{ 
        mb: 2,
        display: 'flex',
        alignItems: 'center'
      }}
      action={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {saving && <CircularProgress size={20} />}
          <Button 
            size="small" 
            variant="outlined"
            onClick={handleDiscard}
            disabled={saving}
            sx={{
              color: '#8B4513',
              borderColor: '#8B4513',
              '&:hover': {
                borderColor: '#A0522D',
                backgroundColor: 'rgba(139, 69, 19, 0.04)',
              }
            }}
          >
            Discard
          </Button>
          <Button 
            size="small" 
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      }
    >
      {error ? `Error: ${error}` : 'You have unsaved changes'}
    </Alert>
  );
};

export default SaveBar;

